import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Lightning } from "@phosphor-icons/react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { project, contribOf } from "@/lib/finance";
import { ContribInput } from "@/components/ContribInput";
import { usd, changeClass } from "@/lib/format";

export function QuickWhatIf({ profile, balance }) {
  const [contrib, setContrib] = useState(null);
  const [ageDelta, setAgeDelta] = useState(0);
  const c = useMemo(() => contrib ?? (profile ? contribOf(profile) : { mode: "percent", value: 5 }), [contrib, profile]);
  const res = useMemo(() => {
    if (!profile) return null;
    const base = project(profile, balance).final.expected;
    const hyp = project(profile, balance, { contrib: c, retireAge: profile.retire_age + ageDelta }).final.expected;
    return { base, hyp };
  }, [profile, balance, c, ageDelta]);
  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button className="rounded-full gap-2" data-testid="quick-whatif-open-button"><Lightning size={16} weight="fill" /> Quick What-If</Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:max-w-md" style={{ background: "var(--glass-bg)", backdropFilter: "blur(20px)" }} data-testid="quick-whatif-drawer">
        <SheetHeader><SheetTitle className="font-display">Quick What-If</SheetTitle></SheetHeader>
        {res && (
          <div className="mt-8 space-y-8">
            <ContribInput profile={profile} value={c} onChange={setContrib} testid="quick-contrib" />
            <div>
              <div className="flex justify-between text-sm mb-3"><span>Retirement age</span><span className="font-mono-num" data-testid="quick-age-value">{profile.retire_age + ageDelta}</span></div>
              <Slider min={-5} max={10} step={1} value={[ageDelta]} onValueChange={([v]) => setAgeDelta(v)} data-testid="quick-age-slider" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="glass p-4"><p className="text-xs text-muted-foreground">Current strategy</p><p className="font-mono-num text-lg font-semibold mt-1" data-testid="quick-base-balance">{usd(res.base)}</p></div>
              <div className="glass p-4"><p className="text-xs text-muted-foreground">Hypothetical</p><p className="font-mono-num text-lg font-semibold mt-1 text-grad-money" data-testid="quick-hyp-balance">{usd(res.hyp)}</p></div>
            </div>
            <p className={`text-sm font-mono-num ${changeClass(res.hyp - res.base)}`} data-testid="quick-delta">{res.hyp >= res.base ? "+" : ""}{usd(res.hyp - res.base)} at retirement</p>
            <Link to="/simulator" className="text-sm text-cyan-700 dark:text-cyan-400 underline" data-testid="quick-open-simulator-link">Open full simulator</Link>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
